import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Form, {
  Item,
  RequiredRule,
  CompareRule,
  ButtonItem,
  ButtonOptions,
  Label,
  CustomRule,
} from 'devextreme-react/form';
import LoadIndicator from 'devextreme-react/load-indicator';
import notify from 'devextreme/ui/notify';
import "./../ResetPassword/ResetPassword.css";

const { REACT_APP_SERVER_URL } = process.env;

export default function SetNewPassword() {
  const history = useHistory();
  const location = useLocation();
  const formRef = useRef(null);

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [token, setToken] = useState(null);
  const [passwordMode, setPasswordMode] = useState('password');
  const [confirmPasswordMode, setConfirmPasswordMode] = useState('password');

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    if (!resetToken) {
      notify({
        message: 'אסימון איפוס סיסמה חסר או לא תקין.',
        type: 'error',
        displayTime: 5000,
        width: 'auto'
      }, 'center', 'top');
      setTimeout(() => history.push('/login'), 5000);
    }
    setToken(resetToken);
  }, [location, history]);

  const onFormOptionChanged = useCallback((e) => {
    if (e.name === 'formData') {
      setFormData(e.value);
    }
  }, []);

  const handleSubmit = async () => {

    if (!token) {
      return;
    }

    const form = formRef.current?.instance;
    const result = form?.validate();


    if (!result?.isValid) {
      return;
    }

    setLoading(true);

    try {
      const apiUrl = `${REACT_APP_SERVER_URL}resetpassword/setnew`;

      const requestBody = {
        Token: token,
        NewPassword: formData.password
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });


      if (!response.ok) {
        let errorMessage = `Error: ${response.status} ${response.statusText}`;

        try {
          const errorData = await response.json();
          errorMessage = errorData.Message || errorData.message || errorMessage;
        } catch (jsonError) {
          console.error("Could not parse error response as JSON", jsonError);
        }
        throw new Error(errorMessage);
      }

      setFormSubmitted(true);

    } catch (error) {
      console.error('Error setting new password:', error);
      notify(
        error.message,
        'error',
        3000
      );
    } finally {
      setLoading(false);
    }
  };

  const navigateToLogin = () => {
    history.push('/login');
  };

  const togglePasswordMode = () => {
    setPasswordMode(prev => prev === 'password' ? 'text' : 'password');
  };

  const toggleConfirmPasswordMode = () => {
    setConfirmPasswordMode(prev => prev === 'password' ? 'text' : 'password');
  };

  const passwordEditorOptions = useMemo(() => ({
    placeholder: 'הקלד סיסמה חזקה',
    mode: passwordMode,
    elementAttr: { dir: 'ltr' },
    buttons: [{
      name: 'togglePassword',
      location: 'after',
      options: {
        icon: passwordMode === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash',
        stylingMode: 'text',
        onClick: togglePasswordMode,
      },
    }],
  }), [passwordMode]);

  const confirmPasswordEditorOptions = useMemo(() => ({
    placeholder: 'הקלד את הסיסמה שוב',
    mode: confirmPasswordMode,
    elementAttr: { dir: 'ltr' },
    buttons: [{
      name: 'toggleConfirmPassword',
      location: 'after',
      options: {
        icon: confirmPasswordMode === 'password' ? 'fas fa-eye' : 'fas fa-eye-slash',
        stylingMode: 'text',
        onClick: toggleConfirmPasswordMode,
      },
    }],
  }), [confirmPasswordMode]);

  return (
    <div className="reset-password-page">
      <div className="form-container">
        <div className="form-content">
          <div className="form-header">
            <span className="form-icon">🔑</span>
            <h2>קביעת סיסמה חדשה</h2>
            <p>הזן את סיסמתך החדשה בשדות הבאים.</p>
          </div>


          {formSubmitted ? (
            <div className="success-message">
              <p><strong>הסיסמה עודכנה בהצלחה! ✅</strong></p>
              <p>כעת באפשרותך להתחבר עם סיסמתך החדשה.</p>
              <div className="form-footer" style={{ borderTop: 'none', marginTop: '16px' }}>
                <button onClick={navigateToLogin}>
                  מעבר למסך ההתחברות
                </button>
              </div>
            </div>
          ) : (
            <div>
              <Form
                ref={formRef}
                formData={formData}
                onOptionChanged={onFormOptionChanged}
                disabled={loading || !token}
              >
                <Item
                  dataField="password"
                  editorType="dxTextBox"
                  editorOptions={passwordEditorOptions}
                >
                  <Label text="סיסמה חדשה" />
                  <RequiredRule message="שדה סיסמה הוא חובה" />
                  <CustomRule
                    message="הסיסמה חייבת להכיל לפחות 6 תווים ולפחות ספרה אחת"
                    validationCallback={(e) => {
                      const value = e.value || '';
                      return value.length >= 6 && /\d/.test(value);
                    }}
                  />
                </Item>

                <Item
                  dataField="confirmPassword"
                  editorType="dxTextBox"
                  editorOptions={confirmPasswordEditorOptions}
                >
                  <Label text="אימות סיסמה" />
                  <RequiredRule message="שדה אימות סיסמה הוא חובה" />
                  <CompareRule
                    message="הסיסמאות אינן תואמות"
                    comparisonTarget={() => formData.password}
                  />
                </Item>

                <ButtonItem>
                  <ButtonOptions
                    width="100%"
                    type="default"
                    disabled={!token}
                    onClick={handleSubmit}
                  >
                    <span className="dx-button-text">
                      {loading ? (
                        <LoadIndicator width="24px" height="24px" visible={true} />
                      ) : (
                        'אישור'
                      )}
                    </span>
                  </ButtonOptions>
                </ButtonItem>
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}